import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import useRefetchOnHostingChange from '../hooks/useRefetchOnHostingChange';
import { getLocale } from '../utils/dateLocale';
import { useNavigate } from 'react-router-dom';
import Menu from '../components/Menu';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoIcon from '@mui/icons-material/Info';
import NotificationContainer from '../components/NotificationContainer';
import { useNotifications } from '../context/NotificationContext';
import { API_BASE } from '../utils/apiConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// CSS для анимации загрузки
const loadingStyles = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = loadingStyles;
  document.head.appendChild(style);
}

// Тестовые данные для запросов
const mockRequests = [
  {
    id: 1,
    user: 'Иван Иванов',
    userEmail: 'ivan@example.com',
    requestType: 'logs',
    requestTypeLabel: 'Запрос логов',
    description: 'Запрос логов устройства ASIC-001 за последние 24 часа',
    device: 'ASIC-001',
    hosting: 'HostingA',
    status: 'pending',
    statusLabel: 'Ожидает',
    createdAt: '2024-01-15T10:30:00Z',
    priority: 'medium'
  },
  {
    id: 2,
    user: 'Петр Петров',
    userEmail: 'petr@example.com',
    requestType: 'restart',
    requestTypeLabel: 'Перезагрузка',
    description: 'Запрос на перезагрузку устройства ASIC-002',
    device: 'ASIC-002',
    hosting: 'HostingB',
    status: 'approved',
    statusLabel: 'Подтвержден',
    createdAt: '2024-01-15T09:15:00Z',
    priority: 'high'
  },
  {
    id: 3,
    user: 'Сергей Сергеев',
    userEmail: 'sergey@example.com',
    requestType: 'pool_change',
    requestTypeLabel: 'Смена пула',
    description: 'Запрос на смену пула для устройства ASIC-003 с stratum+tcp://pool1.com:3333 на stratum+tcp://pool2.com:3333',
    device: 'ASIC-003',
    hosting: 'HostingA',
    status: 'rejected',
    statusLabel: 'Отклонен',
    createdAt: '2024-01-15T08:45:00Z',
    priority: 'low'
  },
  {
    id: 4,
    user: 'Анна Аннова',
    userEmail: 'anna@example.com',
    requestType: 'logs',
    requestTypeLabel: 'Запрос логов',
    description: 'Запрос логов устройства ASIC-004 за последние 7 дней',
    device: 'ASIC-004',
    hosting: 'HostingC',
    status: 'pending',
    statusLabel: 'Ожидает',
    createdAt: '2024-01-15T07:20:00Z',
    priority: 'medium'
  },
  {
    id: 5,
    user: 'Михаил Михайлов',
    userEmail: 'mikhail@example.com',
    requestType: 'restart',
    requestTypeLabel: 'Перезагрузка',
    description: 'Запрос на перезагрузку устройства ASIC-005',
    device: 'ASIC-005',
    hosting: 'HostingB',
    status: 'approved',
    statusLabel: 'Подтвержден',
    createdAt: '2024-01-15T06:30:00Z',
    priority: 'high'
  },
  {
    id: 6,
    user: 'Елена Еленова',
    userEmail: 'elena@example.com',
    requestType: 'pool_change',
    requestTypeLabel: 'Смена пула',
    description: 'Запрос на смену пула для устройства ASIC-006',
    device: 'ASIC-006',
    hosting: 'HostingA',
    status: 'pending',
    statusLabel: 'Ожидает',
    createdAt: '2024-01-15T05:15:00Z',
    priority: 'low'
  }
];

// Функция для получения цвета статуса
const getStatusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'reject':
      return 'error';
    case 'waiting-agent':
      return 'default';
    case 'waiting-confirmation':
      return 'warning';
    case 'completed':
      return 'success';
    case 'done':
      return 'success';
    case 'agent_error':
      return 'error';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

// Функция для получения иконки типа запроса
const getRequestTypeIcon = (requestType) => {
  switch (requestType) {
    case 'log':
      return <AssignmentIcon fontSize="small" />;
    case 'restart':
      return <RefreshIcon fontSize="small" />;
    case 'change_pool':
      return <InfoIcon fontSize="small" />;
    case 'scan':
      return <InfoIcon fontSize="small" />;
    case 'hardreset':
      return <RefreshIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

// Функция для получения читаемого названия типа запроса
const getRequestTypeLabel = (requestType, t) => {
  switch (requestType) {
    case 'log':
      return t('requestsTypeLogs');
    case 'restart':
      return t('requestsTypeRestart');
    case 'change_pool':
      return t('requestsTypeChangePool');
    case 'scan':
      return t('requestsTypeScan');
    case 'hardreset':
      return t('requestsTypeHardreset');
    default:
      return requestType;
  }
};

export default function Requests() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { showSuccess, showError, showWarning } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectedHosting, setSelectedHosting] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', title: '', message: '' });

  // Функция для загрузки данных запросов
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`${API_BASE}requests`, {
        params: {
          page: 1,
          limit: 50,
        },
      });

      // Обрабатываем данные в соответствии с API структурой
      let requestsData = [];
      
      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        requestsData = response.data.results;
        console.log('Requests: Всего запросов:', response.data.total);
      } else {
        console.warn('Requests: Неожиданный формат данных:', response.data);
        requestsData = [];
      }

      setRequests(requestsData);
      console.log('Requests: Полученные данные запросов:', requestsData);
      
    } catch (error) {
      console.error('Ошибка при загрузке данных запросов:', error);
      if (error.response) {
        setError(`${error.response.status}: ${error.response.data.error || error.response.status}`);
      } else {
        setError(t('requestsErrorNetwork'));
      }
      
      // Fallback на тестовые данные при ошибке
      console.log('Requests: Используем тестовые данные как fallback');
      setRequests(mockRequests);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка данных при загрузке страницы
  useEffect(() => {
    fetchRequests();
  }, []);

  useRefetchOnHostingChange(fetchRequests);

  // Функция для принудительного обновления данных
  const handleManualRefresh = () => {
    console.log('Requests: Принудительное обновление данных...');
    fetchRequests();
  };

  // Функция для перехода к профилю пользователя
  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/profile/${userId}`);
    }
  };

  // Функция для перехода к устройству
  const handleDeviceClick = (deviceId) => {
    if (deviceId) {
      navigate(`/device/${deviceId}`);
    }
  };

  // Фильтрация запросов по хостингу
  const filteredRequests = requests.filter(request => 
    !selectedHosting || (request.hosting && request.hosting.name === selectedHosting)
  );

  // Уникальные хостинги для фильтра (динамически из загруженных данных)
  const uniqueHostings = [...new Set(requests
    .filter(request => request.hosting && request.hosting.name)
    .map(request => request.hosting.name)
  )];

  // Обработчик выбора/снятия всех запросов
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRequests(filteredRequests
        .filter(request => request.status === 'waiting-confirmation')
        .map(request => request.id)
      );
    } else {
      setSelectedRequests([]);
    }
  };

  // Обработчик выбора отдельного запроса
  const handleSelectRequest = (requestId) => {
    const request = requests.find(r => r.id === requestId);
    if (request && request.status !== 'waiting-confirmation') {
      return; // Нельзя выбирать запросы не в статусе waiting-confirmation
    }
    
    setSelectedRequests(prev => 
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Получаем только запросы, которые можно подтверждать/отклонять
  const actionableRequests = filteredRequests.filter(request => request.status === 'waiting-confirmation');

  // Проверка, выбраны ли все запросы
  const isAllSelected = actionableRequests.length > 0 && selectedRequests.length === actionableRequests.length;

  // Проверка, выбрана ли часть запросов
  const isPartiallySelected = selectedRequests.length > 0 && selectedRequests.length < actionableRequests.length;

  // Обработчик массового подтверждения
  const handleBulkApprove = () => {
    if (selectedRequests.length === 0) return;
    
    setConfirmDialog({
      open: true,
      action: 'approve',
      title: t('requestsConfirmApproveTitle'),
      message: t('requestsConfirmApproveMessage', { count: selectedRequests.length })
    });
  };

  // Обработчик массового отклонения
  const handleBulkReject = () => {
    if (selectedRequests.length === 0) return;
    
    setConfirmDialog({
      open: true,
      action: 'reject',
      title: t('requestsConfirmRejectTitle'),
      message: t('requestsConfirmRejectMessage', { count: selectedRequests.length })
    });
  };

  // Обработчик подтверждения действия
  const handleConfirmAction = async () => {
    const { action } = confirmDialog;
    const newStatus = action === 'approve' ? 'waiting-agent' : 'reject';
    
    try {
      setProcessing(true);

      const res = await api.patch(
        `${API_BASE}requests`,
        { ids: selectedRequests, status: newStatus }
      );
      const results = res?.data?.results || [];
      const updatedIds = new Set(results.filter(r => r.status === 'updated').map(r => r.id));
      
      // Обновляем статус выбранных запросов
      const updatedRequests = requests.map(request => (
        updatedIds.has(request.id)
          ? { ...request, status: newStatus }
          : request
      ));
      
      setRequests(updatedRequests);
      setSelectedRequests([]);
      setSuccess(t('requestsStatusUpdated', { status: newStatus }));
      if (typeof res?.data?.updated_count === 'number') {
        showSuccess(t('requestsUpdatedCount', { count: res.data.updated_count }));
      }
      results.forEach((r) => {
        if (r.status === 'updated') {
          showSuccess(r.message || `ID ${r.id}: ${r.from} → ${r.to}`);
        } else if (r.status === 'skipped') {
          showWarning(r.message || `ID ${r.id}: ${r.reason || '-'}`);
        } else if (r.status === 'error') {
          showError(r.message || `ID ${r.id}`);
        }
      });

      // Примечание: при необходимости можно перезагрузить список из API

    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
      setError(t('requestsErrorUpdateStatus'));
      showError(t('requestsErrorUpdateStatus'));
    } finally {
      setProcessing(false);
      setConfirmDialog({ open: false, action: '', title: '', message: '' });
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Обработчик закрытия диалога подтверждения
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, action: '', title: '', message: '' });
  };

  // Обработчик изменения статуса отдельного запроса
  const handleSingleRequestAction = async (requestId, action) => {
    const newStatus = action === 'approve' ? 'waiting-agent' : 'reject';
    
    try {
      setProcessing(true);
      const res = await api.patch(
        `${API_BASE}requests`,
        { ids: [requestId], status: newStatus }
      );
      const results = res?.data?.results || [];
      const updatedIds = new Set(results.filter(r => r.status === 'updated').map(r => r.id));
      
      const updatedRequests = requests.map(request => (
        updatedIds.has(request.id)
          ? { ...request, status: newStatus }
          : request
      ));
      
      setRequests(updatedRequests);
      setSuccess(t('requestsStatusUpdatedSingle', { status: newStatus }));
      if (typeof res?.data?.updated_count === 'number') {
        showSuccess(t('requestsUpdatedCount', { count: res.data.updated_count }));
      }
      results.forEach((r) => {
        if (r.status === 'updated') {
          showSuccess(r.message || `ID ${r.id}: ${r.from} → ${r.to}`);
        } else if (r.status === 'skipped') {
          showWarning(r.message || `ID ${r.id}: ${r.reason || '-'}`);
        } else if (r.status === 'error') {
          showError(r.message || `ID ${r.id}`);
        }
      });

      // Примечание: при необходимости можно перезагрузить список из API

    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
      setError(t('requestsErrorUpdateSingle'));
      showError(t('requestsErrorUpdateSingle'));
    } finally {
      setProcessing(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu />
      <div style={{ padding: '20px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary'
            }}
          >
            <AssignmentIcon 
              sx={{ 
                mr: 2, 
                verticalAlign: 'middle',
                color: theme.palette.mode === 'dark' ? 'white' : 'inherit'
              }} 
            />
            {t('requestsTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('requestsSubtitle')}
          </Typography>
        </Box>

        {/* Фильтры и действия */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('requestsFilterByHosting')}</InputLabel>
                <Select
                  value={selectedHosting}
                  onChange={(e) => setSelectedHosting(e.target.value)}
                  label={t('requestsFilterByHosting')}
                >
                  <MenuItem value="">
                    <em>{t('requestsAllHostings')}</em>
                  </MenuItem>
                  {uniqueHostings.map((hosting) => (
                    <MenuItem key={hosting} value={hosting}>
                      {hosting}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleManualRefresh}
                disabled={loading}
              >
                {t('requestsRefresh')}
              </Button>

              <Chip
                label={t('requestsTotalChip', { count: filteredRequests.length })}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={t('requestsWaitingChip', { count: filteredRequests.filter(r => r.status === 'waiting-confirmation').length })}
                color="warning"
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleBulkApprove}
                disabled={selectedRequests.length === 0 || processing}
              >
                {t('requestsApproveButton', { count: selectedRequests.length })}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleBulkReject}
                disabled={selectedRequests.length === 0 || processing}
              >
                {t('requestsRejectButton', { count: selectedRequests.length })}
              </Button>
            </Box>
          </Box>
        </Paper>

        

        {/* Таблица запросов */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isPartiallySelected}
                      onChange={handleSelectAll}
                      disabled={loading || actionableRequests.length === 0}
                    />
                  </TableCell>
                  <TableCell>{t('requestsColumnUser')}</TableCell>
                  <TableCell>{t('requestsColumnType')}</TableCell>
                  <TableCell>{t('requestsColumnDescription')}</TableCell>
                  <TableCell>{t('requestsColumnDevice')}</TableCell>
                  <TableCell>{t('requestsColumnHosting')}</TableCell>
                  <TableCell>{t('requestsColumnStatus')}</TableCell>
                  <TableCell>{t('requestsColumnCreatedAt')}</TableCell>
                  <TableCell>{t('requestsColumnActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} />
                        <Typography>{t('requestsLoadingData')}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredRequests.length > 0 ? (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id} hover>
                                          <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRequests.includes(request.id)}
                        onChange={() => handleSelectRequest(request.id)}
                        disabled={request.status !== 'waiting-confirmation' || processing}
                      />
                    </TableCell>
                                          <TableCell>
                      <Box>
                        <Tooltip title={request.user?.id ? t('requestsTooltipGoToProfile') : t('requestsTooltipProfileUnavailable')}>
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{
                              cursor: request.user?.id ? 'pointer' : 'default',
                              color: request.user?.id ? 'primary.main' : 'text.primary',
                              '&:hover': request.user?.id ? { textDecoration: 'underline' } : {}
                            }}
                            onClick={() => request.user?.id && handleUserClick(request.user.id)}
                          >
                            {request.user?.name || 'N/A'}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={request.user?.id ? t('requestsTooltipGoToProfile') : t('requestsTooltipProfileUnavailable')}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              cursor: request.user?.id ? 'pointer' : 'default',
                              '&:hover': request.user?.id ? { textDecoration: 'underline' } : {}
                            }}
                            onClick={() => request.user?.id && handleUserClick(request.user.id)}
                          >
                            {request.user?.email || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                                          <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRequestTypeIcon(request.type)}
                        <Typography variant="body2">
                          {getRequestTypeLabel(request.type, t)}
                        </Typography>
                      </Box>
                    </TableCell>
                                          <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {request.type === 'scan' && request.parameters?.ip_ranges ?
                          t('requestsDescIpRanges', { ranges: request.parameters.ip_ranges.join(', ') }) :
                          request.type === 'log' && request.parameters?.time_range ?
                          t('requestsDescPeriod', { start: new Date(request.parameters.time_range.start).toLocaleDateString(), end: new Date(request.parameters.time_range.end).toLocaleDateString() }) :
                          request.type === 'change_pool' && request.parameters?.pools ?
                          t('requestsDescPools', { pools: request.parameters.pools.map(pool => `${pool.url} (${pool.worker})`).join(', ') }) :
                          t('requestsDescNone')
                        }
                      </Typography>
                    </TableCell>
                                          <TableCell>
                      {request.device ? (
                        <Tooltip title={request.device?.id ? t('requestsTooltipGoToDevice') : t('requestsTooltipDeviceUnavailable')}>
                          <Chip 
                            label={request.device.hostname || request.device.ip_address || 'N/A'} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              cursor: request.device?.id ? 'pointer' : 'default',
                              '&:hover': request.device?.id ? { 
                                backgroundColor: 'primary.light',
                                color: 'white'
                              } : {}
                            }}
                            onClick={() => request.device?.id && handleDeviceClick(request.device.id)}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                                          <TableCell>
                      <Typography variant="body2">
                        {request.hosting?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                                          <TableCell>
                      <Chip
                        label={request.status === 'waiting-agent' ? t('requestsStatusWaitingAgent') :
                               request.status === 'waiting-confirmation' ? t('requestsStatusWaitingConfirmation') :
                               request.status === 'completed' ? t('requestsStatusCompleted') :
                               request.status === 'done' ? t('requestsStatusCompleted') :
                               request.status === 'agent_error' ? t('requestsStatusAgentError') :
                               request.status === 'reject' ? t('requestsStatusRejected') :
                               request.status === 'failed' ? t('requestsStatusFailed') :
                               request.status}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                                          <TableCell>
                      <Typography variant="body2">
                        {formatDate(request.created_at)}
                      </Typography>
                    </TableCell>
                                                              <TableCell>
                      {request.status === 'waiting-confirmation' && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('requestsTooltipApprove')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSingleRequestAction(request.id, 'approve')}
                              disabled={processing}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('requestsTooltipReject')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleSingleRequestAction(request.id, 'reject')}
                              disabled={processing}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {request.status !== 'waiting-confirmation' && (
                        <Typography variant="caption" color="text.secondary">
                          {request.status === 'completed' ? t('requestsStatusCompleted') :
                           request.status === 'done' ? t('requestsStatusCompleted') :
                           request.status === 'failed' ? t('requestsStatusFailed') :
                           request.status === 'agent_error' ? t('requestsStatusAgentError') :
                           request.status === 'reject' ? t('requestsStatusRejected') :
                           request.status === 'waiting-agent' ? t('requestsStatusWaitingAgent') :
                           request.status}
                        </Typography>
                      )}
                    </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {error ? t('requestsErrorLoadData') : t('requestsNoData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Сообщение если запросов нет */}
        {!loading && filteredRequests.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('requestsNotFoundTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedHosting ? t('requestsNotFoundForHosting', { hosting: selectedHosting }) : t('requestsNoActive')}
            </Typography>
          </Paper>
        )}
      </div>

      <NotificationContainer />
      {/* Диалог подтверждения */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            {t('usersCancelButton')}
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={loading}
          >
            {loading ? t('requestsProcessing') : (confirmDialog.action === 'approve' ? t('requestsTooltipApprove') : t('requestsTooltipReject'))}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
